import React, { useCallback, useState } from 'react';
import { TypedMemo } from '../../common/types';
import { useGetSmartLinkQuery } from '../../../redux/api/fymAPI';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';
import { LoadingView } from '../../common/views/LoadingView';
import { SmartLinkManageViewRenderer } from './SmartLinkManageViewRenderer';
import { EmbedSmartLinkModal } from '../embedSmartLinkModal/EmbedSmartLinkModal';
import { useDeleteSmartLinkFlow } from '../hooks';
import { openExternal } from '../../../utils/openExternal';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { isSmartLinkUnavailableError, SmartLinkPageIds, smartLinkPageIdsOf } from '../unavailable/smartLinkPageIds';
import { SmartLinkUnavailableView } from '../unavailable/SmartLinkUnavailableView';
import { usePreviewPageLang } from '../smlPreview/usePreviewPageLang';

interface SmartLinkManageViewProps {
  route?: {
    params: {
      accountId: number;
      smartLinkId: number;
    };
  };
}

const SmartLinkManageContent: React.FC<SmartLinkPageIds> = ({ accountId, smartLinkId }) => {
  const router = useSMLRouter();
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState<boolean>(false);
  const withLang = usePreviewPageLang();

  const {
    data: smartLinkResponse,
    isLoading: isGetSmartLinkLoading,
    error: getSmartLinkError,
  } = useGetSmartLinkQuery({ accountId, smartLinkId });

  // The dialog reports on the page, so it is opened with the page in hand.
  const { openSmartLinkDeletionModal, isDeleteSmartLinkLoading, deleteSmartLinkError, SmartLinkDeletionModal } =
    useDeleteSmartLinkFlow(accountId, smartLinkId, smartLinkResponse?.smartLink);

  const goToSmartLinkDetailsView = useCallback(() => {
    router.push(SMLRoutes.details, { accountId, smartLinkId });
  }, [router, accountId, smartLinkId]);

  const goToSmartLinkDuplicationView = useCallback(() => {
    router.push(SMLRoutes.duplicate, { accountId, smartLinkId });
  }, [router, accountId, smartLinkId]);

  // "Change address" is the address field of the page's own form, which is
  // where the slug has always been edited.
  const goToSmartLinkAddressEdition = useCallback(() => {
    router.push(SMLRoutes.edit, { accountId, smartLinkId });
  }, [router, accountId, smartLinkId]);

  const closeEmbedModal = useCallback(() => setIsEmbedModalOpen(false), []);

  const openWeb = useCallback(() => {
    if (!smartLinkResponse?.smartLink?.shareUrl) {
      return;
    }
    openExternal(withLang(smartLinkResponse.smartLink.shareUrl)).catch((e) => console.log(e));
  }, [smartLinkResponse?.smartLink?.shareUrl, withLang]);

  const openEmbedModal = useCallback(() => setIsEmbedModalOpen(true), []);

  // A page of another workspace, a deleted page or a wrong id: one clean state, not the raw answer.
  if (isSmartLinkUnavailableError(getSmartLinkError)) {
    return <SmartLinkUnavailableView />;
  }
  const error = getSmartLinkError ?? deleteSmartLinkError;
  if (error) {
    return <ErrorView error={error} />;
  }

  if (!smartLinkResponse || isGetSmartLinkLoading || isDeleteSmartLinkLoading) {
    return <LoadingView />;
  }

  const { smartLink } = smartLinkResponse;
  const disabledEmbedCode = smartLink.publishedSmartLinkVersion === null;
  // The published name, not the draft one: the embed serves the live page.
  const pageName = smartLink.publishedSmartLinkVersion?.name ?? smartLink.recentSmartLinkVersion.name;

  return (
    <>
      {smartLink.embedUrl && (
        <EmbedSmartLinkModal
          isOpen={isEmbedModalOpen}
          embedUrl={smartLink.embedUrl}
          accountId={accountId}
          pageName={pageName}
          closeModal={closeEmbedModal}
        />
      )}
      {SmartLinkDeletionModal}
      <SmartLinkManageViewRenderer
        smartLink={smartLink}
        openEmbedModal={openEmbedModal}
        openInWeb={openWeb}
        onDelete={openSmartLinkDeletionModal}
        goToSmartLinkDetailsView={goToSmartLinkDetailsView}
        goToSmartLinkDuplicationView={goToSmartLinkDuplicationView}
        goToSmartLinkAddressEdition={goToSmartLinkAddressEdition}
        disabledEmbedCode={disabledEmbedCode}
      />
    </>
  );
};

/**
 * The manage page of one SmartLink. A missing or bad id (`accountId=abc`) shows the "not available"
 * state and sends no request (QA finding brk-2).
 */
const SmartLinkManageViewComponent: React.FC<SmartLinkManageViewProps> = ({ route }) => {
  const ids = smartLinkPageIdsOf(useQueryParam('accountId', route), useQueryParam('smartLinkId', route));
  if (ids === null) {
    return <SmartLinkUnavailableView />;
  }
  return <SmartLinkManageContent accountId={ids.accountId} smartLinkId={ids.smartLinkId} />;
};

export const SmartLinkManageView = TypedMemo(SmartLinkManageViewComponent);
